import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { addDays, can, type AwayCounts, type MyScheduleAvailability, type SessionSummary, type TeamDetail } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { formatLongDay, today } from '../../lib/dates';
import { Alert, Button, Empty, LoadingBlock, Section } from '../ui';
import { SessionCard } from '../../pages/SchedulePage';
import { NewSessionDialog } from './NewSessionDialog';
import { useAwayCounts, useMyAvailability, useMyPlayers } from '../availability/Availability';

/** A team's sessions: everything coming up, and the last few months. */
export function TeamSessions({ team }: { team: TeamDetail }) {
  const [creating, setCreating] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const t = today();
  const upcoming = useQuery({
    queryKey: ['team-sessions', team.id, 'upcoming', t],
    queryFn: () => api.get<SessionSummary[]>(`/teams/${team.id}/sessions?from=${t}&to=${addDays(t, 365)}`),
  });
  const past = useQuery({
    queryKey: ['team-sessions', team.id, 'past', t],
    queryFn: () => api.get<SessionSummary[]>(`/teams/${team.id}/sessions?from=${addDays(t, -365)}&to=${addDays(t, -1)}&order=desc`),
    enabled: showPast,
  });
  const canPlan = can(team.role, 'sessions:write');
  // Answers and counts for the next four months (later sessions: answer on their own page).
  const myPlayers = useMyPlayers();
  const linkedHere = (myPlayers.data ?? []).some((p) => p.teamId === team.id);
  const mine = useMyAvailability(t, addDays(t, 120), linkedHere);
  const out = useAwayCounts(t, addDays(t, 120), can(team.role, 'sessions:details'));

  return (
    <div>
      <Section
        title="Coming up"
        aside={
          canPlan && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden /> New session
            </Button>
          )
        }
      >
        {upcoming.isLoading ? (
          <LoadingBlock />
        ) : upcoming.isError ? (
          <Alert>{errorMessage(upcoming.error)}</Alert>
        ) : !upcoming.data?.length ? (
          <Empty title="No sessions coming up">{canPlan ? 'Plan the next training session for this team.' : 'Your coach hasn’t published any upcoming sessions.'}</Empty>
        ) : (
          <SessionList list={upcoming.data} mine={mine.data} out={out.data} />
        )}
      </Section>
      <Section title="Past sessions">
        {!showPast ? (
          <button className="text-[14px] font-medium text-action hover:underline" onClick={() => setShowPast(true)}>
            Show past sessions
          </button>
        ) : past.isLoading ? (
          <LoadingBlock />
        ) : past.isError ? (
          <Alert>{errorMessage(past.error)}</Alert>
        ) : !past.data?.length ? (
          <p className="text-muted">No sessions in the last year.</p>
        ) : (
          <SessionList list={past.data} />
        )}
      </Section>
      <NewSessionDialog open={creating} onClose={() => setCreating(false)} teamId={team.id} />
    </div>
  );
}

function SessionList({ list, mine, out }: { list: SessionSummary[]; mine?: MyScheduleAvailability; out?: AwayCounts }) {
  return (
    <ol className="space-y-3">
      {list.map((s) => (
        <li key={s.id} className="grid gap-x-4 gap-y-1 sm:grid-cols-[180px_1fr]">
          <p className="pt-3 text-[14px] font-medium">{formatLongDay(s.date)}</p>
          <SessionCard s={s} showTeam={false} mine={mine?.sessions[s.id]} out={out?.sessions[s.id]} />
        </li>
      ))}
    </ol>
  );
}
